import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../Global/Layout';
import DataTable from '../../Global/DataTable';
import { showError } from '../../../utils/sweetAlertConfig';
import { API_CONFIG, MSG_REMARK } from '../../../contexts/constants';
import { LottoBallsDisplay } from '../../../utils/LottoBalls';
import { formatTHB } from '../../../utils/numberFormat';
import { formatDateLong, formatDateTable } from '../../../utils/dateFormatter';

const Results = ({ onLogout, userRole }) => {
  const [loading, setLoading] = useState(false);
  const [prizeData, setPrizeData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [awardInfo, setAwardInfo] = useState(null);
  const [selectedRpid, setSelectedRpid] = useState(null);

  useEffect(() => {
    fetchResultsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedRpid) {
      fetchResultsDetail(selectedRpid);
    }
  }, [selectedRpid]);

  	if (!['sysadmin', 'owner', 'admin', 'agent', 'customer'].includes(userRole)) {
		window.location.href = '/forbidden';
		return null;
	}

	const fetchResultsList = async () => {

		const urlParams = new URLSearchParams(window.location.search);
		const rpidParam = urlParams.get('rpid');

		if (rpidParam) {
			setSelectedRpid(rpidParam);
		} else {
			try {
			setLoading(true);
			const response = await axios.get(`${API_CONFIG.BASE_URL}/results/list`, {
				params: { type: 'powerball', orderStatus: 'all', status: 'active' },
				headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
			});

			if (response.data.success && Array.isArray(response.data.data)) {
				// Auto-select latest result if available
				if (response.data.data.length > 0) {
					setSelectedRpid(response.data.data[0].id);
				}
			} else {
				console.error('Invalid results list data format');
			}
			} catch (error) {
			console.error('Error fetching results list:', error);
			handleFetchError(error);
			} finally {
			setLoading(false);
			}
		}
	};

  const fetchResultsDetail = async (rpid) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_CONFIG.BASE_URL}/results/detail`, {
        params: { rpid },
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.data.success && response.data.data) {
        setAwardInfo(response.data.data.awardInfo);
        setPrizeData(response.data.data.prizeData || []);
      } else {
        console.error('Invalid results detail data format');
        setPrizeData([]);
        setAwardInfo(null);
      }
    } catch (error) {
      console.error('Error fetching results detail:', error);
      handleFetchError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (error) => {
    let errorMessage = 'ไม่สามารถโหลดข้อมูลผลรางวัลได้';

    if (error.response) {
      errorMessage = error.response.data?.message || `เกิดข้อผิดพลาด (${error.response.status})`;

      if (error.response.status === 401 || error.response.status === 403) {
        errorMessage = error.response.data?.message || 'กรุณาเข้าสู่ระบบใหม่';
        showError('เซสชันหมดอายุ', errorMessage);
        setTimeout(() => {
          localStorage.removeItem('token');
          onLogout();
        }, 1500);
        return;
      }
    } else if (error.request) {
      errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้';
    }

    showError('เกิดข้อผิดพลาด', errorMessage);
    setPrizeData([]);
    setAwardInfo(null);
  };

  const columns = [
    // {
	// 	key: 'no',
	// 	header: 'ลำดับ',
	// 	className: 'text-center',
	// 	width: 100,
	// 	render: (row) => (
	// 		<div className="flex gap-2 justify-center">{row.no}</div>
	// 	)
	// },
    {
      	key: 'match',
      	header: 'ลักษณะรางวัล Match',
		render: (row) => {
			let _ballSet = [];
			for (let index = 0; index < row.whiteBalls; index++) {
				_ballSet.push('');
			}
			return <LottoBallsDisplay grayBalls={_ballSet} specialBall={row.hasRedBall} type="powerball" wh={10} />
		}
    },
    {
      key: 'powerballWinners',
      header: 'ผู้ถูกรางวัล',
      render: (row) => (
        <div className="text-center">
          {typeof row.powerballWinners === 'number' ? row.powerballWinners.toLocaleString() : row.powerballWinners}
        </div>
      )
    },
    {
      key: 'powerballPrize',
      header: 'มูลค่าเงินรางวัล (บาท)',
      render: (row) => (
        <div className="text-center font-semibold">{formatTHB(row.powerballPrize)}</div>
      )
    },
  ];

  const totalPages = Math.max(1, Math.ceil(prizeData.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const gotoPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setCurrentPage(p);
  };

  // Slice data for current page
  const startIndex = (safeCurrentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedPrizeData = prizeData.slice(startIndex, endIndex);

  return (
    <Layout title="ผลรางวัล : Powerball" onLogout={onLogout} userRole={userRole}>

		<div className="mb-4">
			<p className="text-gray-600 dark:text-gray-400">
			{awardInfo?.prizeAnnouncementDate ? `ประจำวันที่ ${formatDateLong(awardInfo.prizeAnnouncementDate)}` : '...'}
			</p>
			{MSG_REMARK.PRIZE_EXCHANGE_RATE}
      	</div>

      {/* Top grid: Winning Numbers | Winners | Video */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 justify-center gap-4">
        {/* Winning Numbers */}
        <div className="bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark overflow-hidden">
          <div className="px-4 py-3 border-b border-borderc-light dark:border-borderc-dark bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90">
            <h2 className="text-xl font-semibold text-black dark:text-white">เลขที่ออก</h2>
          </div>
          <div className="p-4">
            {/* Date placeholder (no API date available) */}
            <div className="text-center text-gray-700 dark:text-gray-200 font-semibold mb-4">
              {awardInfo?.prizeAnnouncementDate ? formatDateTable(awardInfo.prizeAnnouncementDate) : '...'}
            </div>

            {/* Balls row (static placeholders to match visual) */}
            <div className="flex items-center justify-center gap-2 mb-4">
              {awardInfo?.winBall && awardInfo?.winBallSpecial ? (
                <LottoBallsDisplay
                  grayBalls={awardInfo.winBall}
                  specialBall={awardInfo.winBallSpecial}
                  type="powerball"
                  wh={10}
                />
              ) : (
                <div className="text-gray-500">...</div>
              )}
			</div>

            {/* Power Play pill */}
            <div className="flex justify-center mb-4 hidden">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-red-600 text-white font-semibold shadow-sm">Power Play 2x</span>
            </div>

            {/* Estimated jackpot + cash value (converted to THB) */}
            <div className="space-y-1 text-center">
              <div className="text-gray-700 dark:text-gray-200">
                เงินรางวัลแจ็กพอตโดยประมาณ<br/>
				<span className="font-bold text-red-600 text-xl">
                  {awardInfo?.jackpotBath ? formatTHB(awardInfo.jackpotBath) : '-'} บาท
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Video */}
        <div className="sm:col-span-2 lg:col-span-1 bg-white dark:bg-surface-dark border border-borderc-light dark:border-borderc-dark rounded-xl shadow-card dark:shadow-carddark overflow-hidden ">
          <div className="px-4 py-3 border-b border-borderc-light dark:border-borderc-dark bg-blue-50 dark:bg-[#2a2a2a] text-black dark:text-white/90">
            <h2 className="text-xl font-semibold text-black dark:text-white">วิดีโอ</h2>
          </div>
          <div className="p-4">
            <div className="w-full aspect-video bg-black/5 dark:bg-white/5 rounded-lg overflow-hidden min-h-64 max-h-80">
              {awardInfo?.youtubeUrl ? (
                <iframe
                  className="w-full h-full"
				  src={`https://www.youtube.com/embed/${awardInfo.youtubeUrl}`}
                  title="Powerball Drawing Video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                ></iframe>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500">
                  ไม่มีวิดีโอ
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

	    <div className="mb-4">
        <p className="text-gray-600 dark:text-gray-400">
          ตารางแสดงผลรางวัล จำนวนผู้ถูกรางวัล และเงินรางวัลในแต่ละประเภท
        </p>
      </div>

      <DataTable
        columns={columns}
        data={paginatedPrizeData}
        loading={loading}
        page={safeCurrentPage}
        pageSize={pageSize}
        totalItems={prizeData.length}
        onPageChange={gotoPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        emptyMessage="ไม่มีข้อมูลผลรางวัล"
		showPagination={false}
      />
    </Layout>
  );
};

export default Results;
